import { useUIStore } from '@/stores';
import { useInventoryDataStore } from '@/stores/inventory/inventory-data-store';
import { useTradesDataStore } from '@/stores/trades/trades-data-store';
import type { User } from '@/types';
import inventoryPreview from './previews/inventory-table.svg';
import inventorySearchMp4 from './previews/inventory-search.mp4';
import inventorySearchWebm from './previews/inventory-search.webm';
import inventorySearchPoster from './previews/inventory-search.png';
import inventoryFiltersMp4 from './previews/inventory-filters.mp4';
import inventoryFiltersWebm from './previews/inventory-filters.webm';
import inventoryFiltersPoster from './previews/inventory-filters.png';
import inventoryColumnsMp4 from './previews/inventory-columns.mp4';
import inventoryColumnsWebm from './previews/inventory-columns.webm';
import inventoryColumnsPoster from './previews/inventory-columns.png';
import inventorySavedViewsMp4 from './previews/inventory-saved-views.mp4';
import inventorySavedViewsWebm from './previews/inventory-saved-views.webm';
import inventorySavedViewsPoster from './previews/inventory-saved-views.png';
import rateDetailsRebateRatePng from './previews/rate-details-rebate-rate.png';
import rateDetailsFeePng from './previews/rate-details-fee.png';
import positionDatesOpenPng from './previews/position-dates-open.png';
import positionDatesEvergreenPng from './previews/position-dates-evergreen.png';
import positionDatesTermPng from './previews/position-dates-term.png';
import { tourSelector, waitForElement } from '../utils/wait-for-element';
import { makeTourSyntheticKeyEvent } from '../utils/synthetic-key-event';
import type { TourStep } from '../types';

/**
 * Highest tour version currently shipped. Bump together with adding a new step
 * (whose `addedInVersion` matches the new value). Users with
 * `user.onboardingSeenVersion < CURRENT_TOUR_VERSION` are eligible for the
 * "What's new" mini-tour built from steps where `addedInVersion > seenVersion`.
 */
export const CURRENT_TOUR_VERSION = 7;

const greetingName = (user: User | null): string => {
  const raw = user?.name?.trim();
  if (!raw) return '';
  return raw.split(/[\s@]/)[0] ?? '';
};

const ensureFilterSidebarOpen = () => {
  useUIStore.getState().setShowFilterSidebar(true);
  useUIStore.getState().setFilterSidebarCollapsed(false);
};

const INVENTORY_FILTERS_TOUR_PREVIEW_IDS = ['counterparty', 'quantity'] as const;
const expandFiltersForTour = (ids: readonly string[]) => {
  const expand = useUIStore.getState().expandFilter;
  ids.forEach((id) => expand(id));
};
const collapseFiltersForTour = (ids: readonly string[]) => {
  const collapse = useUIStore.getState().collapseFilter;
  ids.forEach((id) => collapse(id));
};
const switchFilterSidebarTab = (tab: 'filters' | 'columns') => {
  ensureFilterSidebarOpen();
  useUIStore.getState().setSidebarActiveTab(tab);
  useUIStore.getState().setColumnsTabActive(tab === 'columns');
};
const switchInventoryTab = (
  tab: 'inventory' | 'availability' | 'counterparty-reports',
) => {
  useInventoryDataStore.getState().setActiveInventoryTab(tab);
};

/**
 * Minimal navigate signature used by tour steps. Compatible with
 * tanstack-router's `useNavigate()` return value at the call sites we use.
 * Kept narrow on purpose — tour-steps must not import the router module to
 * avoid a circular dependency (router → routes → Layout → TourProvider →
 * tour-steps).
 */
type TourNavigate = (opts: {
  to: string;
  params?: Record<string, unknown>;
  search?: (prev: Record<string, unknown>) => Record<string, unknown>;
  replace?: boolean;
}) => Promise<void> | void;

const buildNavigateToReportTab = (navigate: TourNavigate | undefined) =>
  async (reportTab: 'availability' | 'recall') => {
    switchInventoryTab('counterparty-reports');
    if (!navigate) return;
    await navigate({
      to: '/inventory',
      search: (prev) => ({
        tab: 'counterparty-reports',
        cp: prev.cp,
        reportTab,
        view_id: prev.view_id,
      }),
      replace: true,
    });
  };

/**
 * Switch the inventory page's main tab AND clean up the URL search params so
 * a stale `tab=counterparty-reports` from a prior frame does not bleed into
 * the next one. The store-only `switchInventoryTab` helper is enough for
 * forward navigation, but Back transitions land on a frame whose onEnter ran
 * after the URL had been mutated by a later frame — without this helper, the
 * page-level useEffect that reads the URL on mount has nothing to work with
 * and the wrong tab can persist.
 */
const buildSwitchInventoryTabAndUrl = (navigate: TourNavigate | undefined) =>
  async (tab: 'inventory' | 'availability') => {
    switchInventoryTab(tab);
    if (!navigate) return;
    await navigate({
      to: '/inventory',
      search: (prev) => {
        if (tab === 'availability') {
          return { tab: 'availability' };
        }
        return { view_id: prev.view_id };
      },
      replace: true,
    });
  };

/**
 * Trade-surface navigation helpers for the tour. The trade steps drive
 * navigation through `onEnter` (not the static `route` field) because the mode
 * lives in a search param and the single-trade page needs a dynamic `tradeId`.
 * Each helper re-establishes its page on every frame enter, so stepping Back
 * across a step boundary still lands on the right view.
 */
const buildNavigateToTrades =
  (navigate: TourNavigate | undefined) => async () => {
    if (!navigate) return;
    await navigate({ to: '/trades', search: () => ({}), replace: true });
  };

// LR-587: the marking workspace moved to Collateral Manager. Structure per the
// 12.08 product spec: GMRA → Cash Pool (repo margin call), GMSLA → Cash Pool
// Fee / Cash Rebate (marking).
const buildNavigateToCollateral =
  (navigate: TourNavigate | undefined) =>
  async (product: 'gmra' | 'gmsla') => {
    if (!navigate) return;
    await navigate({
      to: '/collateral',
      search: () => ({ product, leg: product === 'gmra' ? 'cash' : 'fee' }),
      replace: true,
    });
  };

const buildNavigateToHistorySubtab =
  (navigate: TourNavigate | undefined) =>
  async (htab: 'trades' | 'mtm') => {
    if (!navigate) return;
    await navigate({
      to: '/trades',
      search: () => ({ mode: 'history', htab }),
      replace: true,
    });
  };

/**
 * DOM helpers for trade-surface steps. The tour drives two interactions the
 * store cannot reach — row selection and run-log expansion are local component
 * state — by clicking the rendered elements, mirroring the counterparty-create
 * step which already clicks its button in onEnter.
 */
// Blotter rows, but not while the table is busy: a loading selection shows the
// previous selection's rows, inert.
const TRADE_ROWS = '[data-trades-table]:not([aria-busy]) [data-row-id]';

const clickFirstTradeRow = async () => {
  const row = await waitForElement(TRADE_ROWS, 2500);
  (row as HTMLElement | null)?.click();
};

const clearTradeRowSelection = () => {
  // useRowSelection clears on Escape via a document-level listener. Dispatch on
  // document.body so e.target is an element with `closest` (the handler guards
  // against dialogs that way) and the event still bubbles up to the listener.
  // The event is TAGGED (makeTourSyntheticKeyEvent) so TourProvider's own
  // window-level Escape handler ignores it — an untagged synthetic Escape would
  // bubble up there and skip() the whole tour (LR-446).
  document.body.dispatchEvent(makeTourSyntheticKeyEvent('Escape'));
};

/**
 * Switch the single-trade page's active tab by clicking its tab button. The
 * tabs are `<button id="tab-analytics|activity|trade-terms" role="tab">` and the
 * active panel below carries `data-tour="trade-detail-tab-content"` — the tour
 * spotlights the tab strip + that panel together so the body copy lands on real
 * content. Mirrors the other tour interactions the store can't reach (row
 * selection, MTM run expansion) by clicking the rendered element.
 */
const switchTradeDetailTab = async (
  tab: 'analytics' | 'activity' | 'trade-terms',
) => {
  const btn = await waitForElement(`#tab-${tab}`, 2500);
  (btn as HTMLElement | null)?.click();
};

/**
 * Open the single-trade page for the first ACTIVE trade (falling back to the
 * first row). Navigates DIRECTLY to `/trades/$tradeId` — the trade id is read
 * straight off the blotter row's `data-row-id`, so we don't depend on selecting
 * a row and the FloatingActionBar rendering.
 *
 * Why the navigate is RE-ASSERTED in a loop: the trades view->URL sync
 * (`use-url-view-sync`) could revert a freshly-committed `/trades/:id`
 * transition back to the blotter while it was still settling — earlier
 * attempts (FAB Open, settle delays) chased that race indirectly. The sync is
 * now guarded against firing during a pending transition (the real fix), but
 * we keep a bounded re-assert here as belt-and-suspenders: if we ever detect
 * we bounced back to the list, navigate again. Skips entirely when already on a
 * single-trade page (a later frame of this same step) so frame transitions
 * don't re-navigate.
 */
const buildOpenFirstTrade =
  (navigate: TourNavigate | undefined) => async () => {
    if (!navigate) return;
    const onDetail = () => window.location.pathname.startsWith('/trades/');
    if (onDetail()) return;
    // Ensure the live blotter is showing (e.g. stepping Back from MTM/History,
    // where the mode lives in search params), then wait for its rows to render.
    // Any rows, busy or not, mean the blotter is already showing.
    if (!document.querySelector('[data-trades-table] [data-row-id]')) {
      await navigate({ to: '/trades', search: () => ({}), replace: true });
    }
    const firstRow = await waitForElement(TRADE_ROWS, 2500);
    if (!firstRow) return;
    const rows = Array.from(
      document.querySelectorAll<HTMLElement>(TRADE_ROWS),
    );
    // First Active row (richest single-trade view); fall back to the first row.
    const target =
      rows.find((r) => /\bActive\b/.test(r.textContent ?? '')) ?? rows[0];
    const tradeId = target?.getAttribute('data-row-id');
    if (!tradeId) return;
    // Drive the navigation and confirm it stuck; re-assert on a bounce-back.
    // `navigate` resolves after the transition commits, so window.location is
    // already settled — in the common (no-bounce) case we return immediately
    // without the settle delay; the wait + retry only kick in if we detect a
    // bounce back to the list.
    for (let attempt = 0; attempt < 4; attempt++) {
      await navigate({ to: '/trades/$tradeId', params: { tradeId } });
      if (onDetail()) return;
      await new Promise((r) => setTimeout(r, 250));
    }
  };

const openTradeForm = () => {
  useTradesDataStore.getState().setIsCreateTradeModalOpen(true);
};
const closeTradeForm = () => {
  useTradesDataStore.getState().setIsCreateTradeModalOpen(false);
};

/**
 * Scroll the open trade-form modal so the bottom edge of the targeted section
 * lands at ~ratio (default 60%) of the form's visible height. With Floating
 * UI's placement: 'bottom' on each frame, the popup sits in the lower 40% at
 * a stable Y across consecutive frames — the spotlight bottom and the card
 * stay put while the spotlit section above changes (top of the spotlight
 * shrinks as later sections are smaller).
 *
 * Uses an instant (non-animated) scroll and waits one paint so the form's
 * scroll position is settled before the apply effect reads the target's
 * rect — a previous smooth-scroll variant raced the apply's positioning,
 * leaving the card overlapping the spotlit section. Frames that call this
 * also set `disableScrollIntoView` so the apply effect's automatic
 * `scrollIntoView({block: 'center'})` does not fight the manual placement.
 */
const scrollTradeFormSectionToY = async (sel: string, ratio = 0.6) => {
  const target = (await waitForElement(tourSelector(sel), 1500)) as HTMLElement | null;
  if (!target) return;
  const formEl = target.closest('form') as HTMLElement | null;
  if (!formEl) return;
  const desiredY = formEl.clientHeight * ratio;
  const scrollTop = Math.max(0, target.offsetTop + target.offsetHeight - desiredY);
  // 'instant' is the standardized non-animated value; falling back via the
  // ScrollBehavior cast keeps TypeScript happy on lib targets that have not
  // picked up the new union member yet.
  formEl.scrollTo({ top: scrollTop, behavior: 'instant' as ScrollBehavior });
  await new Promise<void>((resolve) =>
    requestAnimationFrame(() => resolve()),
  );
};

/**
 * Pin the trade-form scroll to its tail so both the Trade Summary section
 * and the action row beneath it (Cancel / Create Trade) are inside the
 * visible area at the same time. `scrollTradeFormSectionToY` cannot do this
 * cleanly: the Summary section is followed by Comments + footer rows, so
 * positioning Summary at any ratio of the form pushes those siblings out
 * of view. Bottom-aligning the form is the only mode that keeps the whole
 * tail visible at once.
 */
const scrollTradeFormToBottom = async () => {
  const target = (await waitForElement(
    tourSelector('trade-form-section-summary'),
    1500,
  )) as HTMLElement | null;
  if (!target) return;
  const formEl = target.closest('form') as HTMLElement | null;
  if (!formEl) return;
  formEl.scrollTo({
    top: formEl.scrollHeight,
    behavior: 'instant' as ScrollBehavior,
  });
  await new Promise<void>((resolve) =>
    requestAnimationFrame(() => resolve()),
  );
};

export const getTourSteps = (
  user: User | null,
  navigate?: TourNavigate,
): TourStep[] => {
  const firstName = greetingName(user);
  const navigateToReportTab = buildNavigateToReportTab(navigate);
  const switchInventoryTabAndUrl = buildSwitchInventoryTabAndUrl(navigate);
  const navigateToTrades = buildNavigateToTrades(navigate);
  const navigateToCollateral = buildNavigateToCollateral(navigate);
  const navigateToHistorySubtab = buildNavigateToHistorySubtab(navigate);
  const openFirstTrade = buildOpenFirstTrade(navigate);
  return [
    {
      id: 'welcome',
      addedInVersion: 1,
      route: '/inventory',
      title: firstName ? `Welcome to LendRail, ${firstName}` : 'Welcome to LendRail',
      body: 'Find inventory, create trades, and manage counterparty reports — all in one place. We will walk you through it in under a minute.',
    },
    {
      id: 'inventory-overview',
      addedInVersion: 1,
      route: '/inventory',
      // Targets a 55%-tall sentinel pinned to the top of InventoryPage —
      // not the full scrollable table. Placement bottom puts the card
      // under the spotlit upper half.
      targetSelector: 'inventory-overview',
      placement: 'bottom',
      title: 'Your inventory',
      body: 'Every position you hold, live. Rows are securities; columns show quantity, price, and lending status. Under Lent to Counterparties, hover any cell to see borrowed quantity, EUR amount, rate, and S&P-delta in one tooltip.',
      onEnter: () => {
        // Defensive: if the user navigated to a non-Inventory inventory-page
        // tab (Availability / CptyReports) in a previous session, snap back
        // so the spotlight lands on the actual inventory table.
        switchInventoryTab('inventory');
      },
      frames: [
        {
          // Frame 0 inherits step.title + step.body via resolveFrame's
          // fallback — no duplication needed.
        },
        {
          title: 'Statement',
          body: 'Pick any past day from the calendar to load historical inventory — positions, rates, and balances as they stood that day.',
          targetSelector: 'inventory-statement',
          secondarySelector: 'inventory-statement-dropdown',
          placement: 'left',
          onEnter: async () => {
            ensureFilterSidebarOpen();
            const host = await waitForElement(tourSelector('inventory-statement'), 2000);
            if (!host) return;
            if (!useUIStore.getState().dateTimePickerOpen) {
              host.querySelector('button')?.click();
            }
            const dropdown = await waitForElement(
              '.fixed.shadow-xl.rounded-lg.bg-white.border-gray-200',
              500,
            );
            dropdown?.setAttribute('data-tour', 'inventory-statement-dropdown');
          },
          onLeave: () => {
            const dropdown = document.querySelector<HTMLElement>(
              tourSelector('inventory-statement-dropdown'),
            );
            dropdown?.removeAttribute('data-tour');
            if (!useUIStore.getState().dateTimePickerOpen) return;
            const host = document.querySelector<HTMLElement>(tourSelector('inventory-statement'));
            host?.querySelector('button')?.click();
          },
        },
        {
          title: 'Search',
          body: 'Searches across every column at once and highlights the matches in each row. Combines with the filters on the right.',
          targetSelector: 'inventory-search',
          placement: 'bottom',
          media: {
            type: 'video',
            sources: [
              { src: inventorySearchMp4, mime: 'video/mp4' },
              { src: inventorySearchWebm, mime: 'video/webm' },
            ],
            poster: inventorySearchPoster,
            alt: 'Typing in the search box filters the table and highlights matching cells in every column',
            aspect: '16:9',
          },
        },
      ],
    },
    {
      id: 'inventory-filters',
      addedInVersion: 1,
      layout: 'split',
      route: '/inventory',
      title: 'Inventory tools',
      targetSelector: 'inventory-filters',
      // `left` centers the card vertically against the filter sidebar
      // spotlight — the sidebar is tall enough that anchoring to its
      // bottom edge (`left-end`) parked the card near the page footer
      // instead of next to the controls it describes.
      placement: 'left',
      previewImage: inventoryPreview,
      docsLink: 'inventory',
      body: 'Narrow down by ticker, exchange, sector, and more. Filters update the table live.',
      onEnter: () => {
        switchInventoryTab('inventory');
        switchFilterSidebarTab('filters');
        expandFiltersForTour(INVENTORY_FILTERS_TOUR_PREVIEW_IDS);
      },
      onLeave: () => {
        collapseFiltersForTour(INVENTORY_FILTERS_TOUR_PREVIEW_IDS);
        switchFilterSidebarTab('filters');
      },
      frames: [
        {
          title: 'Inventory filters',
          body: 'Narrow down by ticker, exchange, sector, quantity, and more — filters update the table live, and the CSV button below exports your current view. Right-click any column header for a quick filter on that column, or right-click a cell to filter by its exact value. Click Applied filters above the table to review what is currently in effect and remove individual filters or all at once.',
          media: {
            type: 'video',
            sources: [
              { src: inventoryFiltersMp4, mime: 'video/mp4' },
              { src: inventoryFiltersWebm, mime: 'video/webm' },
            ],
            poster: inventoryFiltersPoster,
            alt: 'Selecting a counterparty filter and a quantity range — the inventory table filters live.',
            aspect: '9:16',
          },
        },
        {
          title: 'Columns',
          body: 'Hide columns, pin them to the left or right, and drag them into any order — either here in the sidebar or directly in the table header. While the Columns tab is open you are in edit mode; every change saves automatically as you go.',
          // Spotlight the entire sidebar (same target as the Filters frame)
          // so the card stays put across frames — only the body copy and
          // the active sidebar tab change. Less visual jumping.
          targetSelector: 'inventory-filters',
          placement: 'left',
          media: {
            type: 'video',
            sources: [
              { src: inventoryColumnsMp4, mime: 'video/mp4' },
              { src: inventoryColumnsWebm, mime: 'video/webm' },
            ],
            poster: inventoryColumnsPoster,
            alt: 'Opening the Columns panel and dragging Quantity to reorder it.',
            aspect: '3:4',
          },
          onEnter: () => switchFilterSidebarTab('columns'),
        },
        {
          title: 'Saved views',
          body: 'Snapshot your filters, columns, and sort into a named view. Save updates your current view; Save as new keeps the original and creates a copy. Make a view private to just you or share it with the team, and star one to set it as your default — it loads automatically every new session.',
          targetSelector: 'inventory-saved-views',
          placement: 'bottom',
          media: {
            type: 'video',
            sources: [
              { src: inventorySavedViewsMp4, mime: 'video/mp4' },
              { src: inventorySavedViewsWebm, mime: 'video/webm' },
            ],
            poster: inventorySavedViewsPoster,
            alt: 'Naming a new saved view in the Save View dialog.',
            aspect: '3:4',
          },
          docsLink: 'savedViews',
          onEnter: () => switchFilterSidebarTab('filters'),
        },
      ],
    },
    {
      // Inventory is already covered in the inventory-overview step earlier
      // in the tour, and Counterparty Reports moved into the dedicated
      // cpty-reports-deep-dive step below — leaving Availability Report as
      // the only sibling of Inventory worth introducing here.
      id: 'availability-report',
      addedInVersion: 1,
      route: '/inventory',
      targetSelector: 'availability-overview',
      placement: 'bottom',
      title: 'Availability Report',
      body: 'Availability is the daily snapshot of what each prime broker says is borrowable. It uses the same configurable columns, filters, and saved views as Inventory. Statement loads any past day from the calendar, and the download button gives you either the full morning report or a filtered slice tailored for a specific client.',
      // Switch the main tab AND clean the URL — Back from the next step
      // (counterparty-reports) leaves a stale `tab=counterparty-reports`
      // search param that otherwise keeps the page on the wrong tab even
      // after the store flips to availability.
      onEnter: () => switchInventoryTabAndUrl('availability'),
    },
    {
      id: 'cpty-reports-deep-dive',
      addedInVersion: 1,
      route: '/inventory',
      // Frame 0 spotlights the Counterparty Reports tab button; subsequent
      // frames target the availability and recall sub-tab sentinels. Each
      // sub-tab's sentinel uses a distinct `data-tour` value so the
      // `findVisible()` filter cannot latch onto the wrong one if both ever
      // co-render (current behaviour: only one is mounted at a time).
      targetSelector: 'counterparty-reports-tab-button',
      placement: 'bottom',
      title: 'Counterparty Reports',
      body: 'Counterparty Reports is per-counterparty.',
      docsLink: 'counterpartyReports',
      onEnter: () => navigateToReportTab('availability'),
      frames: [
        {
          title: 'Counterparty Reports',
          body: 'Counterparty Reports is per-counterparty. For each one you maintain a configurable availability and recall report — your own filters, columns, and saved views per counterparty — that gets sent to the broker on schedule.',
          // Spotlight only the tab button so the introduction frame reads
          // as "this top-level section" rather than "this table" — the
          // detail frames below take over the table-level focus.
          targetSelector: 'counterparty-reports-tab-button',
          placement: 'bottom',
          media: { type: 'none' },
        },
        {
          title: 'Counterparty availability report',
          body: 'The report you send to a counterparty. Pick one from the list and customise what they see — same filters, columns, and saved views as Inventory, scoped here. The countdown shows the next auto-send; Send Now pushes immediately, CSV exports for manual sharing.',
          targetSelector: 'cpty-availability-overview',
          // Pull the Availability sub-tab pill into the spotlight so the
          // user sees that the report they are looking at lives under that
          // sub-tab — without this, the sub-tab row sits in the dimmed
          // area and reads as unrelated chrome.
          secondarySelector: 'cpty-availability-subtab',
          placement: 'bottom',
          media: { type: 'none' },
        },
        {
          title: 'Recall report',
          body: 'A separate scheduled report listing the securities you are recalling from this counterparty. Same configurable columns and filters as availability; same auto-send schedule with manual override. Switch between availability and recall sub-tabs at the top of the workspace.',
          targetSelector: 'cpty-recall-overview',
          // Same rationale as the availability frame above.
          secondarySelector: 'cpty-recall-subtab',
          placement: 'bottom',
          media: { type: 'none' },
          onEnter: () => navigateToReportTab('recall'),
        },
      ],
    },
    {
      id: 'trades-overview',
      addedInVersion: 1,
      targetSelector: 'trades-overview',
      placement: 'bottom',
      title: 'Your trades',
      body: 'Every trade you have created, recent at the top. Configurable filters, columns, an applied-filters chip, and CSV export below.',
      docsLink: 'trades',
      // onEnter (not `route`) re-asserts the live Trades view on every frame so
      // Back from a later trade step lands here correctly.
      onEnter: navigateToTrades,
      frames: [
        {
          // Frame 0 inherits the step title + body and anchors on the
          // page-overview sentinel (step.targetSelector) via resolveFrame.
        },
        {
          title: 'Per-counterparty views',
          body: 'Trades are split into tabs by counterparty — an All Trades tab plus one tab per counterparty that has trades. Switch tabs to focus the book on a single counterparty without losing your place.',
          targetSelector: 'trades-view-tabs',
          placement: 'bottom',
          media: { type: 'none' },
        },
        {
          title: 'Exposure metrics',
          // LR-686: the bar carries three clickable controls now — the two bucket
          // presets and the Attention chip that replaced the Require Attention
          // section of the table. All three toggle the same sidebar filters.
          body: 'The metrics bar splits Stock Lend/Borrow and Repo exposure for the active view. Click Stock L/B or Repo to filter the table to that bucket; click again to clear. The amber Attention chip counts the trades that need you — pending and rejected — and filters down to just those the same way.',
          targetSelector: 'trades-metrics-bar',
          placement: 'bottom',
          media: { type: 'none' },
        },
        {
          // The contextual action bar only renders once a row is selected, and
          // selection is local component state — so the tour navigates to the
          // live Trades view, selects the first row by clicking it (onEnter),
          // spotlights the revealed bar, and clears the selection on the way out.
          title: 'Row actions',
          body: 'Selecting a trade (we picked the first one) reveals a contextual action bar — open the single-trade view, run the valid lifecycle actions for that position, or jump straight to Mark-to-Market for its counterparty.',
          targetSelector: 'trades-floating-action-bar',
          placement: 'top',
          media: { type: 'none' },
          onEnter: async () => {
            await navigateToTrades();
            await clickFirstTradeRow();
          },
          onLeave: clearTradeRowSelection,
        },
      ],
    },
    {
      id: 'single-trade-detail',
      addedInVersion: 2,
      placement: 'bottom',
      title: 'Single trade view',
      body: 'Open any position to its own page.',
      docsLink: 'singleTrade',
      // No `route`: the single-trade URL needs a concrete id, read off the
      // blotter row in onEnter. Frame 0 inherits this step-level onEnter; the
      // three "Dig deeper" frames call openFirstTrade() explicitly, so stepping
      // Back into any of them re-opens the page.
      onEnter: openFirstTrade,
      frames: [
        {
          title: 'Vital signs',
          body: 'The KPI strip leads with the numbers that matter: P&L per day, rate, exposure, mark-vs-booking, and collateral adequacy — the health of the trade at a glance.',
          targetSelector: 'trade-detail-kpi-strip',
          placement: 'bottom',
          media: { type: 'none' },
        },
        // "Dig deeper" walks the three tabs below the KPIs one at a time: each
        // frame switches the active tab and spotlights the tab strip TOGETHER
        // with the panel beneath it (secondarySelector), so the copy lands on
        // the real content. Card sits above the tabs (placement: 'top'), and we
        // re-open the trade first (openFirstTrade is a no-op when already on the
        // page) so stepping Back into any of these frames re-establishes it.
        {
          title: 'Dig deeper — Economics',
          body: 'The Economics tab breaks the trade down by the numbers: accruals to date, running P&L, the rate waterfall, and collateral adequacy.',
          targetSelector: 'trade-detail-tab-nav',
          secondarySelector: 'trade-detail-tab-content',
          placement: 'top',
          media: { type: 'none' },
          onEnter: async () => {
            await openFirstTrade();
            await switchTradeDetailTab('analytics');
          },
        },
        {
          title: 'Dig deeper — Activity',
          body: 'The Activity tab is a timeline of everything that happened to the trade — status changes, settlement events, and accrual postings, newest first.',
          targetSelector: 'trade-detail-tab-nav',
          secondarySelector: 'trade-detail-tab-content',
          placement: 'top',
          media: { type: 'none' },
          onEnter: async () => {
            await openFirstTrade();
            await switchTradeDetailTab('activity');
          },
        },
        {
          title: 'Dig deeper — Trade Terms',
          body: 'The Trade Terms tab is the read-only agreement: the full set of booked terms for this position, exactly as agreed.',
          targetSelector: 'trade-detail-tab-nav',
          secondarySelector: 'trade-detail-tab-content',
          placement: 'top',
          media: { type: 'none' },
          onEnter: async () => {
            await openFirstTrade();
            await switchTradeDetailTab('trade-terms');
          },
        },
      ],
    },
    {
      id: 'mark-to-market',
      addedInVersion: 2,
      placement: 'bottom',
      title: 'Mark to Market',
      body: 'Re-price open positions in bulk.',
      docsLink: 'markToMarket',
      onEnter: () => navigateToCollateral('gmsla'),
      frames: [
        {
          title: 'Mark to Market',
          body: 'A dedicated workspace to re-price open positions in bulk. It lives in Collateral Manager, alongside the cash pools the repo margin call moves.',
          targetSelector: 'mtm-overview',
          placement: 'bottom',
          media: { type: 'none' },
        },
        {
          title: 'Pick a counterparty',
          body: 'Each active counterparty gets a tab; the ones that do not fit are under the menu at the end of the row, which also searches every counterparty, deactivated ones included. Marks are run per counterparty, so start by choosing one.',
          targetSelector: 'mtm-counterparty-tabs',
          placement: 'bottom',
          media: { type: 'none' },
        },
        {
          title: 'Bucket totals',
          // LR-474: totals are split per SETTLEMENT currency — keep the copy in
          // sync with the aggregate bar's bucketing semantics.
          body: 'The aggregate bar shows Stock L/B and Repo totals for the selected counterparty, split per settlement currency. Enter a new price for each position in the grid below — totals and exposure update live.',
          targetSelector: 'mtm-aggregate-bar',
          placement: 'bottom',
          media: { type: 'none' },
        },
        {
          title: 'Run the mark',
          body: 'When the prices look right, Run MTM applies them in one bulk action. A confirmation dialog shows the before/after margined value first.',
          targetSelector: 'mtm-submit-bar',
          placement: 'top',
          media: { type: 'none' },
        },
      ],
    },
    {
      id: 'trade-history',
      addedInVersion: 2,
      placement: 'bottom',
      title: 'History',
      body: 'Look back at terminal trades and past marks.',
      docsLink: 'tradeHistory',
      onEnter: () => navigateToHistorySubtab('trades'),
      frames: [
        {
          title: 'History mode',
          body: 'The History tab is a read-only archive with three sub-tabs: Trades (closed and cancelled positions), Mark to Market (the run log) and Cash Pool (repo margin-call postings).',
          targetSelector: 'trades-mode-tabs',
          placement: 'bottom',
          media: { type: 'none' },
        },
        {
          title: 'Closed & cancelled trades',
          body: 'The Trades sub-tab is your archive of terminal positions — every closed and cancelled trade, with the same configurable columns and filters as the live blotter. The Mark to Market and Cash Pool sub-tabs beside it are the logs, coming up next.',
          // Spotlight the archive table itself (header + first rows) rather than
          // the sub-tab pills, so the user sees the historical book. The
          // History → Trades shell renders the `history-trades-overview`
          // sentinel; the step's onEnter already put us on that sub-tab.
          targetSelector: 'history-trades-overview',
          placement: 'bottom',
          media: { type: 'none' },
        },
        {
          title: 'MTM run log',
          body: 'The Mark-to-Market log is a master-detail view: the left rail lists runs By date or By counterparty, and the pane beside it opens the selected run — its net, its marks, and the price move per instrument. The Cash Pool sub-tab uses the same shell for repo margin-call postings.',
          targetSelector: 'history-mtm-overview',
          placement: 'bottom',
          media: { type: 'none' },
          // LR-683: switching to the sub-tab is enough — the shell selects the
          // first run itself (HistoryShell falls back to `pool[0]`), so the
          // detail pane is already populated when the frame appears. The old
          // accordion this used to expand went away with LR-498.
          onEnter: () => navigateToHistorySubtab('mtm'),
        },
      ],
    },
    {
      id: 'create-trade',
      addedInVersion: 1,
      route: '/trades',
      // Default — frame 0 anchors on the page-header Trade Entry button.
      targetSelector: 'trade-entry-button',
      placement: 'bottom',
      title: 'Create a trade',
      body: 'To open the trade form, click Trade Entry here.',
      docsLink: 'trades',
      requiresPermission: 'create_trades',
      // Co-located cleanup — leaving the step entirely closes the modal so a
      // user who Skips or steps away mid-form doesn't leak it open. Per-frame
      // onEnters open/close the modal as needed during in-step navigation.
      onLeave: () => {
        closeTradeForm();
      },
      frames: [
        {
          title: 'Trade Entry',
          body: 'To open the trade form, click Trade Entry here. You can also kick off a trade directly from any row of an Availability Report — each row has its own Trade Entry action that pre-fills the security and counterparty.',
          targetSelector: 'trade-entry-button',
          placement: 'bottom',
          media: { type: 'none' },
          // Make sure the modal is closed before highlighting the button —
          // a user navigating Back from frame 7.1 would otherwise see the
          // form still on screen on top of the spotlit button.
          onEnter: () => closeTradeForm(),
        },
        {
          title: 'Security & Account',
          body: 'Start with Security — search by symbol, ISIN, SEDOL, or name to pick the instrument. Then in Account Details, pick the counterparty — booking center, custodian, custodian account, and counterparty account fill in automatically from the agreement; override per-trade if needed. The rest of the form unlocks once a security is selected.',
          // Bounding union of Security Search (top of form) and Account
          // Details — covers Security Details too when it's rendered.
          targetSelector: 'trade-form-section-security',
          secondarySelector: 'trade-form-section-account',
          placement: 'bottom',
          media: { type: 'none' },
          // The trade-form sections live inside a modal-internal scroll
          // container; `scrollTradeFormSectionToY` positions them
          // deliberately. Letting the apply effect re-scroll to centre
          // would override that and shift the card off the spotlight.
          disableScrollIntoView: true,
          onEnter: async () => {
            openTradeForm();
            await scrollTradeFormSectionToY('trade-form-section-account');
          },
        },
        {
          title: 'Position Details',
          body: 'Position Details — pick the trade type and collateral type, enter quantity, then margin % (default 100% means no overcollateralisation; e.g. 110% adds a 10% buffer), and tweak gross price if needed. The calculator works out the Contract Value from those inputs.',
          targetSelector: 'trade-form-section-position',
          placement: 'bottom',
          media: { type: 'none' },
          disableScrollIntoView: true,
          onEnter: async () => {
            openTradeForm();
            await scrollTradeFormSectionToY('trade-form-section-position');
          },
        },
        {
          title: 'Rate Details — Rebate Rate',
          body: 'In Rebate Rate mode the borrower keeps the difference between a benchmark and the rebate. Pick the Rate Type — Floating computes Total Rate from a chosen benchmark plus an optional spread (e.g. SOFR + 5bps); Fixed lets you enter the rebate rate directly, with a hint showing how it differs from the benchmark. Pick the dividend pass-through and a trade reason; the calculator works out Daily Earnings.',
          targetSelector: 'trade-form-section-rate',
          placement: 'bottom',
          media: {
            type: 'image',
            src: rateDetailsRebateRatePng,
            alt: 'Rate Details section in Rebate Rate mode: Rate Type Floating, Benchmark SOFR, Spread 5 bps, Total Rate 5.35%, Daily Earnings 2,004.22 USD.',
            aspect: 'auto',
          },
          // Wide compact-card override so the screenshot renders close to
          // the underlying form's width — at 400px the labels are too
          // small to read; at ~1100px (or whatever the viewport allows)
          // the field names match what the user sees in the form below.
          cardWidthClass: 'w-[min(960px,calc(100vw-32px))]',
          disableScrollIntoView: true,
          onEnter: async () => {
            openTradeForm();
            // Image-preview frames use a 960px-wide card with a tall screenshot;
            // anchor section bottom at 40% of form height (instead of the
            // default 60%) so on 1080p viewports there's enough headroom
            // below the spotlight for the card to render without crowding.
            await scrollTradeFormSectionToY('trade-form-section-rate', 0.4);
          },
        },
        {
          title: 'Rate Details — Fee',
          // LR-617: Fee is a stock lend/borrow (GMSLA) mode — the option is not
          // offered for Repo / Reverse Repo, which always book as Rebate.
          body: 'In Fee mode the lender charges a flat lending fee — available on stock lend and borrow trades only. Set Fee % directly. Pick the dividend pass-through and a trade reason; the calculator works out the Total Rate and Daily Earnings from those inputs.',
          targetSelector: 'trade-form-section-rate',
          placement: 'bottom',
          media: {
            type: 'image',
            src: rateDetailsFeePng,
            alt: 'Rate Details section in Fee mode: Fee % 1, Total Rate 4.30%, Daily Earnings 2,023.13 USD.',
            aspect: 'auto',
          },
          cardWidthClass: 'w-[min(960px,calc(100vw-32px))]',
          disableScrollIntoView: true,
          onEnter: async () => {
            openTradeForm();
            await scrollTradeFormSectionToY('trade-form-section-rate', 0.4);
          },
        },
        {
          title: 'Position Dates — Open',
          body: 'Open trades have no scheduled end. Set the Trade Date and Settlement Date; the trade runs until you close it manually.',
          targetSelector: 'trade-form-section-dates',
          placement: 'bottom',
          media: {
            type: 'image',
            src: positionDatesOpenPng,
            alt: 'Position Dates with Trade Duration set to OPEN, showing Trade Date and Settlement Date inputs.',
            aspect: 'auto',
          },
          cardWidthClass: 'w-[min(960px,calc(100vw-32px))]',
          disableScrollIntoView: true,
          onEnter: async () => {
            openTradeForm();
            await scrollTradeFormSectionToY('trade-form-section-dates', 0.4);
          },
        },
        {
          title: 'Position Dates — Evergreen',
          body: 'Evergreen trades roll forward by default. Set Trade Date, Settlement Date, Maturity Date, and the Notice Period (min. termination notice the counterparty must give before unwinding). The calculator works out Total Expected Return and Closing Settlement.',
          targetSelector: 'trade-form-section-dates',
          placement: 'bottom',
          media: {
            type: 'image',
            src: positionDatesEvergreenPng,
            alt: 'Position Dates with Trade Duration set to EVERGREEN, showing Trade Date, Settlement Date, Maturity Date and Notice Period (Days) inputs.',
            aspect: 'auto',
          },
          cardWidthClass: 'w-[min(960px,calc(100vw-32px))]',
          disableScrollIntoView: true,
          onEnter: async () => {
            openTradeForm();
            await scrollTradeFormSectionToY('trade-form-section-dates', 0.4);
          },
        },
        {
          title: 'Position Dates — Term',
          body: 'Term trades have a fixed end. Set Trade Date, Settlement Date, and Maturity Date — the calculator works out Total Expected Return and Closing Settlement based on the duration.',
          targetSelector: 'trade-form-section-dates',
          placement: 'bottom',
          media: {
            type: 'image',
            src: positionDatesTermPng,
            alt: 'Position Dates with Trade Duration set to TERM, showing Trade Date, Settlement Date, Maturity Date inputs and computed Total Expected Return + Closing Settlement.',
            aspect: 'auto',
          },
          cardWidthClass: 'w-[min(960px,calc(100vw-32px))]',
          disableScrollIntoView: true,
          onEnter: async () => {
            openTradeForm();
            await scrollTradeFormSectionToY('trade-form-section-dates', 0.4);
          },
        },
        {
          title: 'Settlement Details',
          // LR-474: the settlement currency is now the PRIMARY display currency
          // across the blotter and trade page — the copy points that out.
          body: 'Settlement Details — delivery type, settlement venue, settlement currency, and FX rate. The settlement currency becomes the primary currency your trade displays in across the blotter. Defaults pull from the counterparty agreement; override per-trade if needed.',
          targetSelector: 'trade-form-section-settlement',
          // Last two sections of the form: scroll them so their TOP sits
          // around 30% of the modal, then place the card ABOVE the spotlight
          // (placement: 'top'). Keeping placement: 'bottom' here would push
          // the card off-screen because there is no room below the section
          // when it is near the form's tail.
          placement: 'top',
          media: { type: 'none' },
          disableScrollIntoView: true,
          onEnter: async () => {
            openTradeForm();
            await scrollTradeFormSectionToY('trade-form-section-settlement', 0.3);
          },
        },
        {
          title: 'Trade Summary & Create',
          body: 'Trade Summary rolls up the calculated values across the whole trade — review the totals, add any optional comments, then Create Trade at the bottom of the form submits.',
          // Bounding union of the Summary section and the form's footer
          // (Cancel / Create Trade) so the spotlight reaches the buttons
          // the body copy is pointing at — without the secondary, the
          // action row sits in the dimmed area and the card can land on
          // top of it when navigating Back from the next step.
          targetSelector: 'trade-form-section-summary',
          secondarySelector: 'trade-form-actions',
          placement: 'top',
          media: { type: 'none' },
          disableScrollIntoView: true,
          onEnter: async () => {
            openTradeForm();
            await scrollTradeFormToBottom();
          },
        },
      ],
    },
    {
      id: 'counterparties',
      addedInVersion: 1,
      route: '/counterparties',
      targetSelector: 'counterparties-overview',
      placement: 'bottom',
      title: 'Counterparties registry',
      body: 'The counterparty register — every counterparty with their agreement type, validity, and status.',
      // Step-level cleanup — leaving the step (Skip / forward to final)
      // closes the modal so it cannot leak open across steps.
      onLeave: () => {
        const close = document.querySelector<HTMLElement>(
          tourSelector('counterparty-modal-close'),
        );
        close?.click();
      },
      frames: [
        {
          title: 'Counterparties registry',
          body: 'The counterparty register — every counterparty with their agreement type, validity, and status. Search runs on the server as you type, and the sidebar narrows the list by agreement type and status; Custodians and Cash Pools live in the tabs beside it.',
          targetSelector: 'counterparties-overview',
          placement: 'bottom',
          media: { type: 'none' },
          // Belt-and-suspenders: close any leftover modals from the trade
          // step (which lives in Layout and persists across navigation) and
          // any open counterparty-agreement modal in case the user navigated
          // Back from the form frame.
          onEnter: () => {
            closeTradeForm();
            const close = document.querySelector<HTMLElement>(
              tourSelector('counterparty-modal-close'),
            );
            close?.click();
          },
        },
        {
          title: 'New Agreement',
          body: 'Click New Agreement to register a counterparty — this kicks off the agreement form.',
          targetSelector: 'counterparty-create-button',
          placement: 'bottom',
          media: { type: 'none' },
          // Closing on enter handles the Back-from-form-frame case too.
          onEnter: () => {
            closeTradeForm();
            const close = document.querySelector<HTMLElement>(
              tourSelector('counterparty-modal-close'),
            );
            close?.click();
          },
        },
        {
          title: 'Agreement form',
          body: 'Two sections: counterparty information and agreement details. Fill what is required and save — the new counterparty appears in the registry.',
          // Spotlight the entire DialogPanel so the user sees the whole
          // form as the target, not just an inset upper-half band. The
          // modal is centered and capped at max-w-4xl, so on tour-eligible
          // viewports (>=1024px) `placement: 'right'` lands the card in
          // the gutter beside the modal; Floating UI's `flip` middleware
          // falls back to `'left'` automatically on narrower screens.
          targetSelector: 'counterparty-form-fields',
          placement: 'right',
          media: { type: 'none' },
          onEnter: () => {
            // Open the modal by clicking the button so React state stays in
            // sync — the page owns `isAddModalOpen`, no global slot to set.
            const btn = document.querySelector<HTMLElement>(
              tourSelector('counterparty-create-button'),
            );
            btn?.click();
          },
        },
      ],
    },
    {
      id: 'final',
      addedInVersion: 1,
      route: '/inventory',
      targetSelector: 'sidebar-take-a-tour',
      placement: 'right',
      title: 'You are all set',
      body: 'You can replay this tour any time from the sidebar.',
      onEnter: async () => {
        // Expand the main sidebar so the spotlit "Take a tour" button is
        // at its full size. Wait out the 300ms width transition before
        // returning so the apply effect's `getBoundingClientRect` reads
        // the post-expansion rect — without the wait, the card anchors to
        // the still-narrow rect and stays there. `stop()` restores the
        // user's pre-tour collapsed preference.
        useUIStore.getState().setSidebarCollapsed(false);
        await new Promise<void>((resolve) => setTimeout(resolve, 350));
      },
    },
  ];
};

